import { AI_JOBS_LIMITS, AI_JOBS_PROTOCOL_VERSION, type AdvancedReviewPrior, type AdvancedReviewSubmitRequest, type AiJobWire, type AiJobsErrorCode, type AiJobsRequest } from '../../supabase/functions/_shared/aiJobsProtocol';
import type { AiV2Photo } from '../../supabase/functions/_shared/aiV2Protocol';
import { currentAccessToken, type AiTransportFailure } from '../backend/aiClient';
import { AI_JOBS_STATUS_TIMEOUT_MS, AI_JOBS_SUBMIT_TIMEOUT_MS, AI_JOBS_UNREGISTER_TIMEOUT_MS, aiJobsBackendConfigured, getAiJobsTransport, type AiJobsCallOutcome } from '../backend/aiJobsClient';
import { advancedThreadForItem, aiJobGroupOf, aiJobNeedsSync, createAiJob, isAiJobActive, lastAnsweredJobOfThread, latestReviewForItem, withJobLocalStatus, withJobServerState, withJobViewed, type AiJobGroup, type AiJobTurn, type V2AiJob } from '../domain/aiJob';
import { CHECKLIST_TEMPLATE_EN_US } from '../domain/checklistTemplates.en-US';
import { isInspectionWorkable, type V2Area, type V2ChecklistItem, type V2Inspection } from '../domain/inspection';
import { nonClientTestMarkerOf } from '../domain/inspectionContext';
import { currentLocale } from '../i18n/i18n';
import { areaDisplayName, itemTitleFor } from '../i18n/labels';
import { accountGuard } from '../persistence/accountScope';
import { putRecords, type RecordWrite } from '../persistence/v2Database';
import { aiPolicyBlockFor, aiStore, buildAreaRequestContext, observationsOfItem, preparePhotosForAi, registerAdvancedSummaryProvider, type AiRunBlock } from './aiStore';
import { applyServerQuota } from './billingStore';
import { createStore } from './createStore';
import { inspectionStore, itemsOf } from './inspectionStore';
import { sessionStore } from './sessionStore';

// ADVANCED AI on the device (Owner contract 2026-09-13 §5–§8, §11–§26, §42–§46; Final Owner Decision
// 2026-09-14 CA-9 / CA-10 / CA-18 / D-CA-6).
//
// The server owns every job (supabase/functions/ai-jobs); this store keeps the device's mirror of
// them — persisted in the yanwushi-v2 database like every other record, so a reload, a restart or a
// lost push changes nothing about what the inspector sees. Rules kept here by construction:
//   - a review is about ONE checklist row and reads ONLY that row's linked photos (§11.2): it never
//     goes to the area's pool, other rows or the whole house for evidence;
//   - a job is submitted ONCE per job id: the id is minted before the upload and travels as the
//     idempotency key, so a double tap, a refresh or a retry never opens a second paid attempt (§18);
//   - a request that got no answer is never called failed: the record stays `submitting` and the
//     next sync asks the server whether the job exists (§20);
//   - nothing here writes an ItemStatus, a note or a report sentence — ever. A result and a chat
//     answer are LAYER 1; only the inspector, through 加入验屋师备注, moves words into the note;
//   - no second network seam: every request to the job service leaves through ONE call inside
//     callAiJobs, with the cloud-AI policy gate immediately in front of it (PP-21, layer 3).
export interface AiJobNotice {
  id: string;
  jobId: string;
  at: number;
}

export interface AiJobsState {
  hydrated: boolean;
  jobs: V2AiJob[];
  // In-app completion notices (§9 "App 正在前台"): shown by the shell, tapped to open the row.
  notices: AiJobNotice[];
  syncing: boolean;
}

export const aiJobsStore = createStore<AiJobsState>({ hydrated: false, jobs: [], notices: [], syncing: false });

export function resetAiJobsStoreForTests(): void {
  aiJobsStore.set(() => ({ hydrated: false, jobs: [], notices: [], syncing: false }));
  resubmittedThisSession.clear();
}

export function applyAiJobsSnapshot(jobs: V2AiJob[]): void {
  aiJobsStore.set((prev) => ({ ...prev, hydrated: true, jobs: [...jobs] }));
}

export function removeAiJobsOfInspectionLocal(inspectionId: string): void {
  aiJobsStore.set((prev) => ({ ...prev, jobs: prev.jobs.filter((j) => j.inspectionId !== inspectionId), notices: prev.notices.filter((n) => prev.jobs.find((j) => j.id === n.jobId)?.inspectionId !== inspectionId) }));
}

function upsertLocal(list: readonly V2AiJob[]): void {
  if (list.length === 0) return;
  aiJobsStore.set((prev) => {
    const byId = new Map(list.map((j) => [j.id, j] as const));
    const kept = prev.jobs.map((j) => byId.get(j.id) ?? j);
    const known = new Set(prev.jobs.map((j) => j.id));
    return { ...prev, jobs: [...kept, ...list.filter((j) => !known.has(j.id))] };
  });
}

async function saveJobs(list: readonly V2AiJob[]): Promise<void> {
  if (list.length === 0) return;
  await putRecords(list.map((value): RecordWrite => ({ store: 'aiJobs', value })));
  upsertLocal(list);
}

function jobById(id: string): V2AiJob | null {
  return aiJobsStore.get().jobs.find((j) => j.id === id) ?? null;
}

// ---------- selectors ----------

export function jobsOfInspection(state: AiJobsState, inspectionId: string): V2AiJob[] {
  return state.jobs.filter((j) => j.inspectionId === inspectionId).sort((a, b) => b.createdAt - a.createdAt);
}

// The row's advanced thread: the deep review and the questions after it (CA-10).
export function threadForItem(state: Pick<AiJobsState, 'jobs'>, itemId: string): AiJobTurn[] {
  return advancedThreadForItem(state.jobs, itemId);
}

export function reviewForItem(state: Pick<AiJobsState, 'jobs'>, itemId: string): V2AiJob | null {
  return latestReviewForItem(state.jobs, itemId);
}

export interface AiJobCounts {
  active: number;
  unread: number;
  viewed: number;
  failed: number;
}

export function jobCountsOf(state: Pick<AiJobsState, 'jobs'>, inspectionId: string | null): AiJobCounts {
  const counts: AiJobCounts = { active: 0, unread: 0, viewed: 0, failed: 0 };
  for (const job of state.jobs) {
    if (inspectionId !== null && job.inspectionId !== inspectionId) continue;
    counts[aiJobGroupOf(job)] += 1;
  }
  return counts;
}

export function jobsInGroup(state: Pick<AiJobsState, 'jobs'>, group: AiJobGroup, inspectionId: string | null): V2AiJob[] {
  return state.jobs.filter((j) => (inspectionId === null || j.inspectionId === inspectionId) && aiJobGroupOf(j) === group).sort((a, b) => b.updatedAt - a.updatedAt);
}

export { aiJobGroupOf, isAiJobActive };

// The row's advanced review summary for the report wording basis (registered with the AI store so
// that store never imports this one).
registerAdvancedSummaryProvider((itemId) => latestReviewForItem(aiJobsStore.get().jobs, itemId)?.result?.summary ?? null);

// ---------- request assembly ----------

interface JobContext {
  inspection: V2Inspection;
  area: V2Area;
  items: V2ChecklistItem[];
}

function contextFor(areaId: string): JobContext | null {
  const state = inspectionStore.get();
  const area = state.areas.find((a) => a.id === areaId);
  if (!area) return null;
  const inspection = state.inspections.find((i) => i.id === area.inspectionId);
  if (!inspection) return null;
  return { inspection, area, items: itemsOf(state, areaId) };
}

function subjectFor(area: V2Area, item: V2ChecklistItem) {
  const locale = currentLocale();
  return { areaName: areaDisplayName(locale, area), itemTitle: itemTitleFor(locale, item) };
}

// The conversation a follow-up carries (CA-10 §12.2): the deep review the thread started with and
// every answered question after it, up to the job this one follows.
function priorFor(job: V2AiJob): AdvancedReviewPrior | null {
  if (!job.followUpOf) return null;
  const thread = advancedThreadForItem(aiJobsStore.get().jobs, job.itemId);
  const root = thread[0]?.job ?? null;
  const rootResult = root?.result ?? null;
  if (!root || !rootResult) return null;
  const turns: { question: string; answer: string }[] = [];
  for (const turn of thread.slice(1)) {
    if (turn.job.id === job.id) break;
    if (turn.question && turn.job.result) turns.push({ question: turn.question, answer: turn.job.result.summary });
  }
  return { summary: rootResult.summary, observations: [...rootResult.observations], risks: [...rootResult.risks], unconfirmed: [...rootResult.unconfirmed], attention: [...rootResult.attention], turns: turns.slice(-AI_JOBS_LIMITS.maxTurns) };
}

export function buildAdvancedReviewRequest(job: V2AiJob, ctx: JobContext, photos: AiV2Photo[], mode: 'new' | 'retry'): AdvancedReviewSubmitRequest | null {
  const context = buildAreaRequestContext(ctx.inspection, ctx.area, ctx.items);
  const item = ctx.items.find((i) => i.id === job.itemId) ?? null;
  if (!item) return null;
  const en = item.templateId ? CHECKLIST_TEMPLATE_EN_US[item.templateId] : undefined;
  const observations = observationsOfItem(aiStore.get(), item.id).map((o) => ({ headline: o.headline, observation: o.observation, concern: o.concern }));
  return {
    protocolVersion: AI_JOBS_PROTOCOL_VERSION,
    action: 'submit',
    mode,
    jobId: job.id,
    tier: 'advanced',
    inspectionId: ctx.inspection.id,
    areaId: ctx.area.id,
    itemId: item.id,
    uiLocale: context.uiLocale,
    reportLocale: context.reportLocale,
    area: context.area,
    // §11.5: the row's identity, its guidance, its result and the inspector's note as they are NOW.
    item: { id: item.id, title: item.title, titleEn: en && en.title !== item.title ? en.title : null, hint: item.hint, status: item.status, inspectorNote: item.inspectorNote },
    checklist: context.checklist,
    scanNote: ctx.area.scanNote,
    question: job.followUpOf ? job.inspectorNote : '',
    aiObservations: observations,
    previousAdvanced: priorFor(job),
    subject: job.subject,
    photos
  };
}

// ---------- THE JOB CHOKEPOINT (PP-21 layer 3 for the advanced tier) ----------

export type AiJobFailureCode = AiTransportFailure | AiJobsErrorCode | AiRunBlock | 'noPhotos' | 'notSignedIn' | 'notConfigured' | 'locked' | 'notFound' | 'imageFailed';

type JobCall = AiJobsCallOutcome | { kind: 'blocked'; code: AiRunBlock };

// The one place a request to the AI job service leaves the device. A SUBMIT carries an inspection's
// photos and notes, so it passes the cloud-AI policy gate here, immediately before the transport —
// an inspection that cannot be resolved is treated as a real client engagement, never waved through.
async function callAiJobs(request: AiJobsRequest, token: string, timeoutMs: number): Promise<JobCall> {
  const sameAccount = accountGuard();
  const subject = request.action === 'submit' ? (inspectionStore.get().inspections.find((i) => i.id === request.inspectionId) ?? null) : null;
  const policyBlock = request.action !== 'submit' ? null : subject ? aiPolicyBlockFor(subject) : 'blockedRealClient';
  if (policyBlock) return { kind: 'blocked', code: policyBlock };
  const outcome = await getAiJobsTransport()(request, { accessToken: token, timeoutMs });
  // The server answers a submit (and a refusal) with the allowance that is left (X-7): the plan page
  // and the AI card keep the server's number — the number of the account that ASKED. An answer that
  // arrives after that account was left counts for nobody on the device (account isolation).
  const quota = outcome.kind === 'response' && 'quota' in outcome.response ? outcome.response.quota : undefined;
  if (quota && sameAccount()) applyServerQuota(quota);
  return outcome;
}

function online(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

// ---------- submit / resubmit ----------

export type SubmitAdvancedOutcome = { ok: true; job: V2AiJob; existing: boolean } | { ok: false; code: AiJobFailureCode; job: V2AiJob | null };

export const AI_JOBS_SUBMIT_RETRY_DELAY_MS = 1_500;

export interface AdvancedReviewInput {
  inspectionId: string;
  areaId: string;
  itemId: string;
  // A follow-up question about the same row (CA-10); '' / absent = the deep review itself.
  question?: string;
  followUpOf?: string | null;
  // Minted by the caller before the tap (one per sheet), so a double tap is ONE job.
  jobId?: string;
  now?: number;
}

// Why an advanced review cannot be started right now — the declarative reason the entry shows.
export function advancedReviewUnavailableReason(input: { photos: number; online: boolean; signedIn: boolean; configured: boolean; locked: boolean; policyBlock: AiRunBlock | null }): AiJobFailureCode | null {
  if (input.policyBlock) return input.policyBlock;
  if (input.locked) return 'locked';
  if (input.photos === 0) return 'noPhotos';
  if (!input.online) return 'offline';
  if (!input.configured) return 'notConfigured';
  if (!input.signedIn) return 'notSignedIn';
  return null;
}

// The deep review of ONE row: its linked photos (§11.2 — never anything else), its note, its guidance.
export async function submitAdvancedReviewAction(input: AdvancedReviewInput): Promise<SubmitAdvancedOutcome> {
  const now = input.now ?? Date.now();
  const ctx = contextFor(input.areaId);
  if (!ctx || ctx.inspection.id !== input.inspectionId) return { ok: false, code: 'notFound', job: null };
  if (!isInspectionWorkable(ctx.inspection)) return { ok: false, code: 'locked', job: null };
  // LAYER 2 (PP-21): refuse before any record, any photo preparation and any network work.
  const policyBlock = aiPolicyBlockFor(ctx.inspection);
  if (policyBlock) return { ok: false, code: policyBlock, job: null };
  const item = ctx.items.find((i) => i.id === input.itemId) ?? null;
  if (!item) return { ok: false, code: 'notFound', job: null };
  const photoIds = [...new Set(item.evidenceIds)].slice(0, AI_JOBS_LIMITS.maxPhotos);
  if (photoIds.length === 0) return { ok: false, code: 'noPhotos', job: null };
  if (sessionStore.get().phase !== 'signedIn') return { ok: false, code: 'notSignedIn', job: null };
  if (!aiJobsBackendConfigured()) return { ok: false, code: 'notConfigured', job: null };
  // Offline: no job is invented (§45) — the entry says the review needs the network.
  if (!online()) return { ok: false, code: 'offline', job: null };
  // The same job id (this sheet's) already exists → the earlier submission stands (§18).
  if (input.jobId) {
    const known = jobById(input.jobId);
    if (known) return known.status === 'notSubmitted' ? resubmitAdvancedReviewAction(known.id, now) : { ok: true, job: known, existing: true };
  }
  const question = (input.question ?? '').trim();
  const job = createAiJob(
    {
      id: input.jobId,
      inspectionId: ctx.inspection.id,
      areaId: ctx.area.id,
      itemId: item.id,
      photoIds,
      inspectorNote: input.followUpOf ? question : item.inspectorNote,
      subject: subjectFor(ctx.area, item),
      contextMarker: nonClientTestMarkerOf(ctx.inspection),
      followUpOf: input.followUpOf ?? null
    },
    now
  );
  await saveJobs([job]);
  return sendJob(job, 'new', now);
}

// A controlled next attempt (§25): the server decides — a completed job answers with its result, a
// running one with itself, only a failed one opens another attempt.
export async function resubmitAdvancedReviewAction(jobId: string, now: number = Date.now()): Promise<SubmitAdvancedOutcome> {
  const job = jobById(jobId);
  if (!job) return { ok: false, code: 'notFound', job: null };
  if (isAiJobActive(job) && job.status !== 'submitting') return { ok: true, job, existing: true };
  const ctx = contextFor(job.areaId);
  if (!ctx) return { ok: false, code: 'notFound', job };
  if (!isInspectionWorkable(ctx.inspection)) return { ok: false, code: 'locked', job };
  if (sessionStore.get().phase !== 'signedIn') return { ok: false, code: 'notSignedIn', job };
  if (!aiJobsBackendConfigured()) return { ok: false, code: 'notConfigured', job };
  if (!online()) return { ok: false, code: 'offline', job };
  return sendJob(job, 'retry', now);
}

async function sendJob(job: V2AiJob, mode: 'new' | 'retry', now: number): Promise<SubmitAdvancedOutcome> {
  // ACCOUNT ISOLATION: the photos are prepared and the answer is awaited for seconds. Once the account
  // this job belongs to was left, nothing of it is sent, written or shown — it is in that account's
  // own database as it was last saved, and its next sign-in settles it with the server (§20).
  const sameAccount = accountGuard();
  const left = (): SubmitAdvancedOutcome => ({ ok: false, code: 'notSignedIn', job: null });
  const failLocal = async (code: AiJobFailureCode, status: V2AiJob['status'] = 'notSubmitted', retryable = true): Promise<SubmitAdvancedOutcome> => {
    if (!sameAccount()) return left();
    const failed = withJobLocalStatus(jobById(job.id) ?? job, status, Date.now(), code, retryable);
    try {
      await saveJobs([failed]);
    } catch {
      upsertLocal([failed]);
    }
    return { ok: false, code, job: failed };
  };
  const ctx = contextFor(job.areaId);
  if (!ctx) return failLocal('notFound');
  const { photos } = await preparePhotosForAi(job.photoIds);
  if (photos.length === 0) return failLocal('imageFailed');
  const request = buildAdvancedReviewRequest(job, ctx, photos, mode);
  if (!request) return failLocal('notFound', 'notSubmitted', false);
  const token = await currentAccessToken();
  if (!token) return failLocal('notSignedIn');
  // The token is the CURRENT session's: one account's photos never travel under another's sign-in.
  if (!sameAccount()) return left();
  await saveJobs([withJobLocalStatus(jobById(job.id) ?? job, 'submitting', Date.now(), null, false)]);
  let outcome = await callAiJobs(request, token, AI_JOBS_SUBMIT_TIMEOUT_MS);
  // The service (its sign-in lookup, its store) was unreachable for a moment: one more try with the
  // SAME job id is safe — the server did not open a job — and spares the inspector a manual retry.
  if (sameAccount() && outcome.kind === 'response' && !outcome.response.ok && outcome.response.retryable && (outcome.response.code === 'unavailable' || outcome.response.code === 'internalError')) {
    await new Promise((r) => setTimeout(r, AI_JOBS_SUBMIT_RETRY_DELAY_MS));
    if (sameAccount()) outcome = await callAiJobs(request, token, AI_JOBS_SUBMIT_TIMEOUT_MS);
  }
  if (!sameAccount()) return left();
  if (outcome.kind === 'blocked') return failLocal(outcome.code, 'notSubmitted', false);
  if (outcome.kind === 'failed') {
    // No answer is not a failure (§20): the server may hold the job. `submitting` + the code stays
    // on the record and the next sync settles it by asking for the job.
    if (outcome.code === 'timeout' || outcome.code === 'network') {
      const pending = { ...withJobLocalStatus(jobById(job.id) ?? job, 'submitting', Date.now(), outcome.code, true) };
      await saveJobs([pending]);
      return { ok: false, code: outcome.code, job: pending };
    }
    return failLocal(outcome.code);
  }
  const response = outcome.response;
  if (!response.ok) {
    // Refused with an answer: the allowance (final) or the service (retryable).
    if (response.code === 'quotaExhausted' || response.code === 'billingUnavailable') return failLocal(response.code, 'failedFinal', response.retryable);
    return failLocal(response.code, 'notSubmitted', response.retryable);
  }
  if (response.action !== 'submit') return failLocal('malformedResponse');
  const merged = withJobServerState(jobById(job.id) ?? job, response.job, Math.max(now, Date.now()));
  await saveJobs([merged]);
  scheduleSyncSoon();
  return { ok: true, job: merged, existing: response.existing };
}

// 继续问高级 AI (CA-10): one more question about the row is a new job that follows the last answered
// one of the row's thread and carries the whole conversation. The photos are the row's linked photos
// as they are NOW (a photo added to ② since is used; one removed is not — §12.6).
export async function askAdvancedFollowUpAction(itemId: string, question: string, now: number = Date.now()): Promise<SubmitAdvancedOutcome> {
  const thread = advancedThreadForItem(aiJobsStore.get().jobs, itemId);
  const parent = lastAnsweredJobOfThread(thread);
  if (!parent) return { ok: false, code: 'notFound', job: null };
  const text = question.trim();
  if (text.length === 0) return { ok: false, code: 'noPhotos', job: null };
  return submitAdvancedReviewAction({ inspectionId: parent.inspectionId, areaId: parent.areaId, itemId, question: text, followUpOf: parent.id, now });
}

// ---------- sync with the server ----------

export type RefreshOutcome = { ok: true; changed: number } | { ok: false; code: AiJobFailureCode };

const resubmittedThisSession = new Set<string>();

function noticeFor(job: V2AiJob, now: number): AiJobNotice {
  return { id: `notice_${job.id}_${now}`, jobId: job.id, at: now };
}

function applyWire(local: V2AiJob | null, wire: AiJobWire, now: number): V2AiJob {
  if (local) return withJobServerState(local, wire, now);
  // A job this device does not know (a reinstall, a second device): kept for the Task Center; it
  // cannot be resubmitted from here (its photos are not here) but its result is.
  const created = createAiJob({ id: wire.jobId, inspectionId: wire.inspectionId, areaId: wire.areaId, itemId: wire.itemId, photoIds: [], inspectorNote: '', subject: wire.subject }, wire.createdAt || now);
  return withJobServerState(created, wire, now);
}

export async function refreshAiJobsAction(options: { force?: boolean; jobIds?: readonly string[]; now?: number } = {}): Promise<RefreshOutcome> {
  const now = options.now ?? Date.now();
  const state = aiJobsStore.get();
  const wantedIds = options.jobIds ? [...options.jobIds] : state.jobs.filter(aiJobNeedsSync).map((j) => j.id);
  if (wantedIds.length === 0 && !options.force) return { ok: true, changed: 0 };
  if (sessionStore.get().phase !== 'signedIn') return { ok: false, code: 'notSignedIn' };
  if (!aiJobsBackendConfigured()) return { ok: false, code: 'notConfigured' };
  if (!online()) return { ok: false, code: 'offline' };
  if (state.syncing) return { ok: true, changed: 0 };
  // ACCOUNT ISOLATION: the jobs the server answers with are the ASKING account's. An answer that
  // arrives after that account was left is dropped — never merged into the next account's list.
  const sameAccount = accountGuard();
  const token = await currentAccessToken();
  if (!token || !sameAccount()) return { ok: false, code: 'notSignedIn' };
  aiJobsStore.set((prev) => ({ ...prev, syncing: true }));
  try {
    const request: AiJobsRequest = { protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'status', jobIds: wantedIds.slice(0, AI_JOBS_LIMITS.maxStatusIds), inspectionId: null };
    const outcome = await callAiJobs(request, token, AI_JOBS_STATUS_TIMEOUT_MS);
    if (!sameAccount()) return { ok: false, code: 'notSignedIn' };
    if (outcome.kind === 'blocked') return { ok: false, code: outcome.code };
    if (outcome.kind === 'failed') return { ok: false, code: outcome.code };
    if (!outcome.response.ok) return { ok: false, code: outcome.response.code };
    if (outcome.response.action !== 'status') return { ok: false, code: 'malformedResponse' };
    const current = aiJobsStore.get();
    const changed: V2AiJob[] = [];
    const notices: AiJobNotice[] = [];
    const returned = new Set<string>();
    for (const wire of outcome.response.jobs) {
      returned.add(wire.jobId);
      const local = current.jobs.find((j) => j.id === wire.jobId) ?? null;
      const next = applyWire(local, wire, now);
      const becameComplete = next.status === 'completed' && (!local || local.status !== 'completed');
      if (!local || local.status !== next.status || local.attempt !== next.attempt || local.errorCode !== next.errorCode || (next.result !== null && local.result === null)) changed.push(next);
      if (becameComplete && next.viewedAt === null) notices.push(noticeFor(next, now));
    }
    // A submission whose answer was lost and that the server does not know: it never arrived.
    for (const id of wantedIds) {
      if (returned.has(id)) continue;
      const local = current.jobs.find((j) => j.id === id);
      if (local && local.status === 'submitting') changed.push(withJobLocalStatus(local, 'notSubmitted', now, local.errorCode ?? 'notFound', true));
    }
    if (changed.length > 0) await saveJobs(changed);
    if (notices.length > 0) aiJobsStore.set((prev) => ({ ...prev, notices: [...prev.notices, ...notices] }));
    // The worker died mid-attempt (§7 / §45): resubmit once per session, with this device's photos.
    for (const job of changed) {
      if (job.status === 'interrupted' && job.retryable && job.photoIds.length > 0 && !resubmittedThisSession.has(job.id)) {
        resubmittedThisSession.add(job.id);
        void resubmitAdvancedReviewAction(job.id, now);
      }
    }
    return { ok: true, changed: changed.length };
  } finally {
    // The flag of the account that asked; a store that was emptied for the next account is not touched.
    if (sameAccount()) aiJobsStore.set((prev) => ({ ...prev, syncing: false }));
  }
}

export function dismissAiJobNotice(id: string): void {
  aiJobsStore.set((prev) => (prev.notices.some((n) => n.id === id) ? { ...prev, notices: prev.notices.filter((n) => n.id !== id) } : prev));
}

// ---------- viewed (§43) ----------

export async function markAdvancedJobViewedAction(jobId: string, now: number = Date.now()): Promise<void> {
  const job = jobById(jobId);
  if (!job) return;
  aiJobsStore.set((prev) => ({ ...prev, notices: prev.notices.filter((n) => n.jobId !== jobId) }));
  if (job.viewedAt !== null) return;
  await saveJobs([withJobViewed(job, now)]);
  // The server's copy of the mark is a convenience for another device; its failure changes nothing.
  if (job.status === 'completed' && sessionStore.get().phase === 'signedIn' && aiJobsBackendConfigured() && online()) {
    void (async () => {
      const token = await currentAccessToken();
      if (token) await callAiJobs({ protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'markViewed', jobId }, token, AI_JOBS_STATUS_TIMEOUT_MS).catch(() => undefined);
    })();
  }
}

// Every completed job of a row's thread is "viewed" once the inspector opened the row's ③.
export async function markThreadViewedAction(itemId: string, now: number = Date.now()): Promise<void> {
  for (const turn of advancedThreadForItem(aiJobsStore.get().jobs, itemId)) {
    if (turn.job.status === 'completed' && turn.job.viewedAt === null) await markAdvancedJobViewedAction(turn.job.id, now);
  }
}

// ---------- push device registration (carries no inspection data) ----------

export async function registerPushDeviceAction(input: { token: string; platform: 'ios' | 'android' | 'web'; environment: 'sandbox' | 'production' | 'unknown'; appVersion: string }): Promise<boolean> {
  if (sessionStore.get().phase !== 'signedIn' || !aiJobsBackendConfigured() || !online()) return false;
  const token = await currentAccessToken();
  if (!token) return false;
  const outcome = await callAiJobs({ protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'registerDevice', ...input }, token, AI_JOBS_STATUS_TIMEOUT_MS);
  return outcome.kind === 'response' && outcome.response.ok && outcome.response.action === 'registerDevice' && outcome.response.registered;
}

// The counterpart, for an account that is signing out of this phone (account isolation 2026-09-21): the
// job service forgets that THIS account owns the token, so a completion push of its jobs never lands on
// the phone the next inspector is using. It needs the account's live session — the server removes the
// CALLER's registration and nobody else's — so it runs before the sign-out (src/state/pushRegistration.ts)
// and is best effort: offline, signed out or unanswered is `false`, never an error. true = the server
// confirmed the account no longer owns the token (whether or not it held it).
export async function unregisterPushDeviceAction(deviceToken: string): Promise<boolean> {
  if (sessionStore.get().phase !== 'signedIn' || !aiJobsBackendConfigured() || !online()) return false;
  const token = await currentAccessToken();
  if (!token) return false;
  const outcome = await callAiJobs({ protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'unregisterDevice', token: deviceToken }, token, AI_JOBS_UNREGISTER_TIMEOUT_MS);
  return outcome.kind === 'response' && outcome.response.ok && outcome.response.action === 'unregisterDevice';
}

// Owner decision list 2026-10-19 items 1 / 2: generating the final report deletes the inspection's process
// data on the server too — its AI review records. true = the server confirmed (also when there were none);
// false = not now (offline, signed out, unanswered, or a function deployed before the action) — the caller
// keeps it queued (src/state/serverPurge.ts).
export async function purgeInspectionOnServer(inspectionId: string): Promise<boolean> {
  if (sessionStore.get().phase !== 'signedIn' || !aiJobsBackendConfigured() || !online()) return false;
  const token = await currentAccessToken();
  if (!token) return false;
  const outcome = await callAiJobs({ protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'purgeInspection', inspectionId }, token, AI_JOBS_UNREGISTER_TIMEOUT_MS);
  return outcome.kind === 'response' && outcome.response.ok && outcome.response.action === 'purgeInspection';
}

// ---------- the sync loop ----------

export const AI_JOBS_POLL_FAST_MS = 5_000;
export const AI_JOBS_POLL_SLOW_MS = 15_000;
export const AI_JOBS_POLL_FAST_WINDOW_MS = 120_000;

let syncStarted = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let unsubscribe: (() => void) | null = null;
let detach: (() => void) | null = null;

function scheduleNext(): void {
  if (!syncStarted) return;
  if (timer) clearTimeout(timer);
  timer = null;
  const pending = aiJobsStore.get().jobs.filter(aiJobNeedsSync);
  if (pending.length === 0) return;
  const oldest = Math.min(...pending.map((j) => j.submittedAt ?? j.createdAt));
  const delay = Date.now() - oldest < AI_JOBS_POLL_FAST_WINDOW_MS ? AI_JOBS_POLL_FAST_MS : AI_JOBS_POLL_SLOW_MS;
  timer = setTimeout(() => {
    timer = null;
    void refreshAiJobsAction().finally(scheduleNext);
  }, delay);
}

function scheduleSyncSoon(): void {
  if (!syncStarted || timer) return;
  scheduleNext();
}

// Starts asking the server about unfinished jobs: on start, whenever the app becomes visible, gets
// focus or comes back online, and on a timer while any job is unfinished (5 s for the first two
// minutes of a job, 15 s afterwards). Idempotent; main.tsx calls it once after boot.
export function startAiJobsSync(): void {
  if (syncStarted) return;
  syncStarted = true;
  const kick = () => {
    void refreshAiJobsAction().finally(scheduleNext);
  };
  if (typeof window !== 'undefined') {
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') kick();
    };
    window.addEventListener('online', kick);
    window.addEventListener('focus', kick);
    document.addEventListener('visibilitychange', onVisible);
    detach = () => {
      window.removeEventListener('online', kick);
      window.removeEventListener('focus', kick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }
  unsubscribe = aiJobsStore.subscribe(() => {
    if (!timer && aiJobsStore.get().jobs.some(aiJobNeedsSync)) scheduleNext();
  });
  kick();
}

export function stopAiJobsSyncForTests(): void {
  syncStarted = false;
  if (timer) clearTimeout(timer);
  timer = null;
  unsubscribe?.();
  unsubscribe = null;
  detach?.();
  detach = null;
}
